// src/pages/CVOptimizer.jsx
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import DashboardLayout from '../components/layout/DashboardLayout';
import { C } from '../styles/tokens';

function ScoreRing({ score, label, color }) {
  const r = 38; const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
      <div style={{ position:'relative', width:90, height:90 }}>
        <svg width="90" height="90" style={{ transform:'rotate(-90deg)' }}>
          <circle cx="45" cy="45" r={r} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="6" />
          <circle cx="45" cy="45" r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition:'stroke-dashoffset 1.4s ease' }} />
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <span className="syne" style={{ fontSize:20, fontWeight:800, color }}>{score}</span>
          <span style={{ fontSize:9, color:'rgba(255,255,255,.35)' }}>/100</span>
        </div>
      </div>
      <span style={{ fontSize:12, color:'rgba(255,255,255,.5)', fontWeight:600 }}>{label}</span>
    </div>
  );
}

const SUGGESTIONS = [
  { icon:'⚠️', level:'high',   text:'Add measurable impact to your work experience (e.g. "Increased conversions by 32%")' },
  { icon:'💡', level:'medium', text:'Include more keywords: "stakeholder management", "agile", "roadmap" are missing' },
  { icon:'✅', level:'low',    text:'Your education section is well-formatted and complete' },
  { icon:'⚠️', level:'high',   text:'Summary section is too short — expand to 3–4 sentences with your value proposition' },
  { icon:'💡', level:'medium', text:'Consider adding certifications relevant to your target roles' },
  { icon:'✅', level:'low',    text:'Contact information is complete and professional' },
];

export default function CVOptimizer() {
  const [uploaded,  setUploaded]  = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed,  setAnalyzed]  = useState(false);
  const [fileName,  setFileName]  = useState('');

  const onDrop = useCallback((files) => {
    if (!files[0]) return;
    setFileName(files[0].name);
    setUploaded(true);
    setAnalyzing(true);
    setTimeout(() => { setAnalyzing(false); setAnalyzed(true); }, 2200);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept:{ 'application/pdf':['.pdf'], 'application/msword':['.doc','.docx'] }, maxFiles:1,
  });

  const scores = [
    { score:73, label:'Overall',  color:C.magenta },
    { score:82, label:'Keywords', color:C.violet  },
    { score:90, label:'Format',   color:C.success },
    { score:68, label:'ATS',      color:C.warning },
    { score:55, label:'Impact',   color:C.info    },
  ];

  const levelColor = { high:C.danger, medium:C.warning, low:C.success };

  return (
    <DashboardLayout title="AI CV Optimizer" subtitle="Upload your CV — our AI parses, scores, and optimizes it instantly">
      <style>{`
        .cv-layout {
          display: grid;
          gap: 20px;
        }
        .cv-layout-analyzed {
          grid-template-columns: 1fr 320px;
        }
        .cv-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .cv-actions button { flex: 1; min-width: 120px; }
        .score-rings {
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          gap: 16px;
        }
        @media (max-width: 900px) {
          .cv-layout-analyzed { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .cv-actions { flex-direction: column !important; }
          .cv-actions button { min-width: unset !important; }
        }
      `}</style>

      <div className={`cv-layout ${analyzed ? 'cv-layout-analyzed' : ''}`}>
        {/* Main panel */}
        <div>
          {/* Upload zone */}
          {!analyzed && !analyzing && (
            <div className="card" style={{ padding:'clamp(24px,5vw,40px)', marginBottom:20 }}>
              <div {...getRootProps()} style={{ border:`2px dashed ${isDragActive ? C.magenta : 'rgba(254,52,232,.25)'}`, borderRadius:14, padding:'clamp(32px,6vw,48px) clamp(20px,4vw,32px)', textAlign:'center', cursor:'pointer', transition:'all .3s', background: isDragActive ? 'rgba(254,52,232,.06)' : 'transparent' }}>
                <input {...getInputProps()} />
                <div style={{ fontSize:40, marginBottom:16 }}>📄</div>
                <h3 className="syne" style={{ fontSize:'clamp(15px,3vw,18px)', fontWeight:700, marginBottom:8 }}>
                  {isDragActive ? 'Drop your CV here' : 'Upload Your CV'}
                </h3>
                <p style={{ fontSize:'clamp(12px,2vw,14px)', color:'rgba(255,255,255,.45)', marginBottom:20 }}>
                  Drag & drop or click to browse<br />
                  <span style={{ fontSize:12, color:'rgba(255,255,255,.3)' }}>PDF, DOC, DOCX up to 5MB</span>
                </p>
                <button style={{ padding:'12px 28px', background:`linear-gradient(135deg,${C.magenta},${C.violet})`, border:'none', borderRadius:9, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'Syne',sans-serif" }}>
                  Browse Files
                </button>
              </div>
            </div>
          )}

          {/* Analyzing */}
          {analyzing && (
            <div className="card" style={{ padding:'clamp(24px,5vw,40px)', textAlign:'center' }}>
              <div style={{ width:60, height:60, borderRadius:'50%', border:`3px solid rgba(255,255,255,.1)`, borderTopColor:C.magenta, animation:'spin .7s linear infinite', margin:'0 auto 20px' }} />
              <h3 className="syne" style={{ fontSize:'clamp(15px,3vw,18px)', fontWeight:700, marginBottom:8 }}>Analyzing {fileName}…</h3>
              <p style={{ fontSize:14, color:'rgba(255,255,255,.45)', marginBottom:20 }}>Our AI is parsing your CV, checking keywords, ATS compatibility, and impact metrics.</p>
              {['Parsing document structure','Extracting keywords','Running ATS simulation','Scoring impact statements'].map((s, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,.05)', maxWidth:360, margin:'0 auto' }}>
                  <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid rgba(255,255,255,.1)`, borderTopColor:C.magenta, animation:'spin .7s linear infinite', animationDelay:`${i*.15}s`, flexShrink:0 }} />
                  <span style={{ fontSize:13, color:'rgba(255,255,255,.55)', textAlign:'left' }}>{s}</span>
                </div>
              ))}
            </div>
          )}

          {/* Results */}
          {analyzed && (
            <>
              {/* Score rings */}
              <div className="card" style={{ padding:'clamp(20px,4vw,28px)', marginBottom:16 }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${C.magenta},${C.violet})` }} />
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
                  <div>
                    <h3 className="syne" style={{ fontSize:17, fontWeight:700 }}>CV Analysis Complete</h3>
                    <p style={{ fontSize:12, color:'rgba(255,255,255,.4)', marginTop:2 }}>Analyzed: {fileName}</p>
                  </div>
                  <span style={{ fontSize:11, padding:'5px 12px', borderRadius:100, background:'rgba(245,158,11,.15)', color:C.warning, fontWeight:700, border:'1px solid rgba(245,158,11,.25)' }}>Needs Optimization</span>
                </div>
                <div className="score-rings">
                  {scores.map(s => <ScoreRing key={s.label} {...s} />)}
                </div>
              </div>

              {/* Suggestions */}
              <div className="card" style={{ padding:'clamp(20px,4vw,28px)', marginBottom:16 }}>
                <h3 className="syne" style={{ fontSize:16, fontWeight:700, marginBottom:18 }}>AI Recommendations</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {SUGGESTIONS.map((s, i) => (
                    <div key={i} style={{ display:'flex', gap:12, padding:'14px 16px', borderRadius:10, background:`${levelColor[s.level]}08`, border:`1px solid ${levelColor[s.level]}20` }}>
                      <span style={{ fontSize:18, flexShrink:0, marginTop:1 }}>{s.icon}</span>
                      <p style={{ fontSize:'clamp(12px,2vw,13.5px)', color:'rgba(255,255,255,.65)', lineHeight:1.6 }}>{s.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="cv-actions">
                <button style={{ padding:'13px', background:`linear-gradient(135deg,${C.magenta},${C.violet})`, border:'none', borderRadius:9, color:'#fff', fontSize:'clamp(12px,2vw,14px)', fontWeight:700, cursor:'pointer', fontFamily:"'Syne',sans-serif" }}>
                  🤖 Auto-Optimize My CV
                </button>
                <button style={{ padding:'13px', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.12)', borderRadius:9, color:'#fff', fontSize:'clamp(12px,2vw,14px)', fontWeight:600, cursor:'pointer' }}>
                  ⬇️ Download Report
                </button>
                <button onClick={() => { setAnalyzed(false); setUploaded(false); setFileName(''); }} style={{ padding:'13px 18px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.09)', borderRadius:9, color:'rgba(255,255,255,.5)', fontSize:13, cursor:'pointer', flexShrink:0, flexGrow:0 }}>
                  Upload New
                </button>
              </div>
            </>
          )}
        </div>

        {/* Tips sidebar — only shown after analysis */}
        {analyzed && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div className="card" style={{ padding:22 }}>
              <h3 className="syne" style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>Quick Wins</h3>
              {[['Start bullet points with action verbs','🚀'],['Quantify achievements with numbers','📊'],['Mirror keywords from job descriptions','🔑'],['Keep to 1–2 pages maximum','📏']].map(([tip,icon]) => (
                <div key={tip} style={{ display:'flex', gap:10, marginBottom:12, alignItems:'flex-start' }}>
                  <span style={{ fontSize:16, flexShrink:0 }}>{icon}</span>
                  <span style={{ fontSize:13, color:'rgba(255,255,255,.55)', lineHeight:1.5 }}>{tip}</span>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding:22 }}>
              <h3 className="syne" style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>ATS Keywords Missing</h3>
              {['stakeholder management','agile methodology','roadmap planning','cross-functional','OKRs','go-to-market'].map(kw => (
                <span key={kw} style={{ display:'inline-block', margin:'4px 4px 0 0', padding:'4px 10px', borderRadius:6, fontSize:12, background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.2)', color:C.danger }}>{kw}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
